import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PropsInitialStateOrderSlice, IMeOrders, INewOrder, IOrderData, PropsBoolean } from "../../types";
import { apiOrder } from "../../utils/apiOrder";
import axios from "axios";

export const newOrder = createAsyncThunk("order/newOrder", async (data: INewOrder, { rejectWithValue, dispatch }) => {
  try {
    const res = await apiOrder().newOrder(data);
    return res.data;
  } catch (e) {
    if (axios.isAxiosError(e) && (e.code === "ECONNABORTED" || e.code === "ETIMEDOUT")) {
      return rejectWithValue(
        "The server is taking longer than expected. We couldn’t confirm whether your request was received. Please check your email before trying again."
      );
    }

    if (axios.isAxiosError(e) && !e.response) {
      return rejectWithValue("We couldn’t connect. Check your internet connection and try again.");
    }

    if (axios.isAxiosError(e) && e.response?.status === 429) {
      return rejectWithValue("Too many attempts were made. Please wait a moment and try again.");
    }

    return rejectWithValue("We couldn’t send your request right now. Please try again in a few minutes.");
  }
});

export const getOrders = createAsyncThunk("order/getOrders", async (_, { rejectWithValue }) => {
  try {
    const res = await apiOrder().getOrders();
    return res.data;
  } catch (e) {
    return rejectWithValue("We couldn’t load the order list. Please try again.");
  }
});

export const getMeOrders = createAsyncThunk(`order/getOwnOrder`, async (_, { rejectWithValue }) => {
  try {
    const res = await apiOrder().getMeOrders();
    const handleMyOrders = (orders: IOrderData[]): IMeOrders[] => {
      return orders.map((item: IOrderData) => {
        const { completed, orderNumber, packets, text } = item;
        return { orderNumber, packets, completed, text };
      });
    };
    return handleMyOrders(res.data);
  } catch (e) {
    return rejectWithValue("We couldn’t load your current orders. Please try again.");
  }
});

const initialState: PropsInitialStateOrderSlice = {
  dataOrders: [],
  meOrders: [],
  confirmSendOrder: false,
  orderSuccessfullySent: false,
  loading: {
    newOrder: false,
    getOrders: false,
    getMeOrders: false,
  },
  error: {
    newOrder: "",
    getOrders: "",
    getMeOrders: "",
  },
};

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    deleteMeOrders: (state) => {
      state.meOrders = [];
    },
    handleOrderSuccessfullySent: (state, action: PropsBoolean) => {
      state.orderSuccessfullySent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(newOrder.pending, (state) => {
      state.loading.newOrder = true;
      state.error.newOrder = "";
    });
    builder.addCase(newOrder.rejected, (state, action: { payload: any }) => {
      state.error.newOrder = action.payload;
      state.loading.newOrder = false;
    });
    builder.addCase(newOrder.fulfilled, (state) => {
      state.loading.newOrder = false;
      state.error.newOrder = "";
      state.confirmSendOrder = true;
    });
    builder.addCase(getOrders.rejected, (state, action: { payload: any }) => {
      state.error.getOrders = action.payload;
    });
    builder.addCase(getOrders.fulfilled, (state, action: { payload: IOrderData[] }) => {
      state.dataOrders = action.payload;
    });
    builder.addCase(getMeOrders.rejected, (state, action: { payload: any }) => {
      state.error.getMeOrders = action.payload;
    });
    builder.addCase(getMeOrders.fulfilled, (state, action: { payload: IMeOrders[] }) => {
      state.meOrders = action.payload;
    });
  },
});

export const { deleteMeOrders, handleOrderSuccessfullySent } = orderSlice.actions;
export default orderSlice.reducer;
